import { ChevronLeft, ChevronRight } from 'lucide-react';
import React, { useState, useEffect, useRef } from 'react';

const AUTO_SLIDE_INTERVAL_MS = 4500; // Slower for reading time (PR 1)
const SWIPE_THRESHOLD_PX = 50;
const PAUSE_AFTER_TOUCH_MS = 3000;

const FeaturesSlider = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [itemsPerView, setItemsPerView] = useState(3);
  const [autoPausedUntil, setAutoPausedUntil] = useState(0);
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);
  
  // Typewriter animation states
  const [currentHeadingIndex, setCurrentHeadingIndex] = useState(0);
  const [displayedText, setDisplayedText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  // Headline color logic (DR 1): first segment = accent (red), middle = neutral (white), last = accent (blue). Applied consistently.
  const headings = [
    [
      { text: "Where Three Generations", color: "text-red-500" },
      { text: " Come Together ", color: "text-white" },
      { text: "Under one Roof.", color: "text-blue-600" }
    ],
    [
      { text: "Unlock", color: "text-red-500" },
      { text: " the Power of the ", color: "text-white" },
      { text: "Eritrean Diaspora", color: "text-blue-600" }
    ]
  ];

  // Get full text for current heading
  const getCurrentFullText = () => {
    return headings[currentHeadingIndex].map(segment => segment.text).join('');
  };
  
  // Feature data - 6 items total
  const features = [
    {
      image: "https://nchs-fe.vercel.app/images/newImages/WhatsApp Image 2025-07-18 at 14.50.03.jpeg",
      title: "Integration i praktiken",
      description: "Vi bygger broar av förståelse, står enade i vår olikhet, och skapar trygghet genom mångkulturell styrka, en levande Svensk-Eritreansk gemenskap där integration blir en gemensam framgång.",
      link: "https://www.erfis.org",
      linkText: "Learn More"
    },
    {
      image: "https://nchs-fe.vercel.app/images/public-diplomacy-page/IMG_4232.JPG",
      title: "Trygghet genom samhörighet",
      description: "Trygghet genom samhörighet. Mångfald förenar oss. Broar mellan kulturer stärker gemenskapen.",
      link: "/nhcc",
      linkText: "Explore"
    },
    {
      image: "https://nchs-fe.vercel.app/images/newImages/WhatsApp Image 2025-07-18 at 14.50.04.jpeg",
      title: "Kultur och mode",
      description: "Med stil och själ bygger vi ett samhälle där alla hör hemma.",
      link: "/festival",
      linkText: "Explore"
    },
    {
      image: "https://nchs-fe.vercel.app/images/newImages/IMG_6654.JPG",
      title: "Inkluderande framtid",
      description: "Ett aktivt föreningsliv bland svensk-eritreanska barn och unga stärker hoppet, gemenskapen och bygger en bättre framtid tillsammans.",
      link: "/festival",
      linkText: "Explore"
    },
    {
      image: "https://nchs-fe.vercel.app/images/newImages/rollerCoaster1.jpg",
      title: "Svensk-eritreanska diasporan:",
      description: "🇸🇪🇪🇷 Den svensk-eritreanska gemenskapen illustrerar hur diaspora-grupper på ett diskret och engagerat sätt kan bidra till samhällsutveckling genom starka interna nätverk och långsiktigt arbete.",
      link: "/nhcc",
      linkText: "Discover"
    },
    {
      image: "https://nchs-fe.vercel.app/images/public-diplomacy-page/20150530-IMG_7397.JPG",
      title: "Broar byggs tillsammans",
      description: "Svensk eritreaner: En kollektiv kraft som bidrar till att förena människor i samhället.",
      link: "/publicdiplomacy",
      linkText: "Explore"
    }
  ];

  // Create an extended array for infinite scroll effect
  const extendedFeatures = [...features, ...features, ...features];

  // Typewriter animation effect
  useEffect(() => {
    const fullText = getCurrentFullText();
    
    const timer = setTimeout(() => {
      if (!isDeleting) {
        // Typing
        if (displayedText.length < fullText.length) {
          setDisplayedText(fullText.substring(0, displayedText.length + 1));
        } else {
          // Wait then start deleting
          setTimeout(() => setIsDeleting(true), 2000);
        }
      } else {
        // Deleting
        if (displayedText.length > 0) {
          setDisplayedText(fullText.substring(0, displayedText.length - 1));
        } else {
          // Move to next heading
          setIsDeleting(false);
          setCurrentHeadingIndex(prev => (prev + 1) % headings.length);
        }
      }
    }, isDeleting ? 50 : 100);

    return () => clearTimeout(timer);
  }, [displayedText, isDeleting, currentHeadingIndex]);

  // Render text with colors
  const renderColoredText = () => {
    const currentHeading = headings[currentHeadingIndex];
    let charCount = 0;
    const result = [];
    
    for (let i = 0; i < currentHeading.length; i++) {
      const segment = currentHeading[i];
      const segmentStart = charCount;
      const segmentEnd = charCount + segment.text.length;
      
      if (displayedText.length > segmentStart) {
        const visiblePart = displayedText.substring(segmentStart, Math.min(segmentEnd, displayedText.length));
        if (visiblePart) {
          result.push(
            <span key={i} className={segment.color}>
              {visiblePart}
            </span>
          );
        }
      }
      
      charCount += segment.text.length;
    }
    
    return result;
  };

  // Update items per view based on screen size
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 640) {
        setItemsPerView(1);
      } else if (window.innerWidth < 1024) {
        setItemsPerView(2);
      } else {
        setItemsPerView(3);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Auto-rotate slides (paused briefly after manual swipe - FR 2)
  useEffect(() => {
    const interval = setInterval(() => {
      if (Date.now() < autoPausedUntil) return;
      setCurrentIndex(prev => {
        const nextIndex = prev + 1;
        if (nextIndex === features.length * 2) {
          return features.length;
        }
        return nextIndex;
      });
    }, AUTO_SLIDE_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [features.length, autoPausedUntil]);

  // Touch handlers for swipe (FR 1 - manual swipe on mobile)
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    setAutoPausedUntil(Date.now() + PAUSE_AFTER_TOUCH_MS);
  };
  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) < SWIPE_THRESHOLD_PX) return;
    if (diff > 0) handleNext();
    else handlePrev();
  };

  // Manual navigation (also resets pause so auto continues after delay)
  const handlePrev = () => {
    setAutoPausedUntil(Date.now() + PAUSE_AFTER_TOUCH_MS);
    setCurrentIndex(prev => {
      const nextIndex = prev - 1;
      if (nextIndex < 0) {
        return features.length * 2 - 1;
      }
      return nextIndex;
    });
  };

  const handleNext = () => {
    setAutoPausedUntil(Date.now() + PAUSE_AFTER_TOUCH_MS);
    setCurrentIndex(prev => {
      const nextIndex = prev + 1;
      if (nextIndex >= features.length * 3) {
        return features.length;
      }
      return nextIndex;
    });
  };

  const transformValue = `translateX(-${(currentIndex * (100 / itemsPerView))}%)`;

  return (
    <section className="py-8 md:py-16 px-4 bg-yellow-400">
      <div className="container mx-auto">
        {/* Typewriter Heading - fixed height on mobile so 2-line wrap doesn't cause layout jerk */}
        <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-center mb-8 md:mb-16 max-w-3xl mx-auto px-4 h-[5rem] md:h-auto md:min-h-[4rem] lg:min-h-[5rem] flex items-center justify-center">
          <span className="inline-block text-left md:text-center">
            {renderColoredText()}
            <span className="animate-pulse text-white">|</span>
          </span>
        </h2>
        
        {/* Slider Container */}
        <div className="relative">
          {/* Slide Indicators - mobile-friendly size (UR 1) */}
          <div className="flex justify-center mb-6 md:mb-8 gap-2 md:gap-3">
            {features.map((_, index) => (
              <button
                key={index}
                onClick={() => {
                  setAutoPausedUntil(Date.now() + PAUSE_AFTER_TOUCH_MS);
                  setCurrentIndex(index + features.length);
                }}
                className={`min-w-[44px] min-h-[44px] md:min-w-0 md:min-h-0 w-3 h-3 md:w-3 md:h-3 mx-0 md:mx-1 rounded-full transition-colors duration-300 flex items-center justify-center ${
                  currentIndex % features.length === index ? 'bg-blue-600 scale-110' : 'bg-gray-300 hover:bg-gray-400'
                }`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>

          {/* Slides - touch swipe (FR 1, UR 2) */}
          <div
            className="overflow-hidden touch-pan-y"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
          >
            <div
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: transformValue }}
            >
              {extendedFeatures.map((feature, index) => (
                <div
                  key={`${feature.title}-${index}`}
                  className={`flex-shrink-0 px-2 md:px-4 ${
                    itemsPerView === 1 ? 'w-full' :
                    itemsPerView === 2 ? 'w-1/2' : 'w-1/3'
                  }`}
                >
                  <a
                    href={feature.link}
                    onClick={(e) => {
                      if (feature.link.startsWith('#')) {
                        e.preventDefault();
                        const el = document.querySelector(feature.link);
                        el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                      }
                    }}
                    className="flex flex-col items-center h-full bg-white rounded-lg shadow-md p-4 md:p-6 block cursor-pointer group focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                  >
                    <div className="mb-4 md:mb-6 w-full flex-1 flex flex-col min-h-0">
                      <div className="w-full pt-[75%] relative rounded-lg overflow-hidden shadow-lg transition-transform duration-300 group-hover:scale-[1.02] group-active:scale-[0.99]">
                        <img
                          src={feature.image}
                          alt={feature.title}
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                      </div>
                    </div>

                    <h3 className="text-lg md:text-xl font-semibold text-center mb-2">
                      {feature.title}
                    </h3>

                    <p className="text-xs md:text-sm text-center text-gray-700 mb-4">
                      {feature.description}
                    </p>

                    <span className="mt-auto inline-flex items-center px-3 py-2 md:px-5 md:py-3 border-2 border-blue-500 text-sm md:text-lg font-semibold rounded-lg overflow-hidden relative bg-transparent group-hover:border-blue-600 transition-colors">
                      <span className="absolute inset-0 w-64 h-0 transition-all duration-700 origin-center rotate-45 -translate-x-16 bg-blue-500 top-1/2 group-hover:h-64 group-hover:-translate-y-32 ease" />
                      <span className="relative text-blue-700 transition duration-700 group-hover:text-white ease">
                        {feature.linkText} <span className="ml-1">›</span>
                      </span>
                    </span>
                  </a>
                </div>
              ))}
            </div>
          </div>
          
          {/* Navigation Arrows - mobile: inside edges for touch (UR 1) */}
          <button
            type="button"
            onClick={handlePrev}
            className="absolute top-1/2 left-2 md:-left-10 -translate-y-1/2 bg-white/90 rounded-full min-w-[44px] min-h-[44px] w-10 h-10 md:w-10 md:h-10 flex items-center justify-center shadow-md z-10 hover:bg-white active:scale-95 transition-all"
            aria-label="Previous slide"
          >
            <ChevronLeft size={24} className="text-gray-800" />
          </button>

          <button
            type="button"
            onClick={handleNext}
            className="absolute top-1/2 right-2 md:-right-10 -translate-y-1/2 bg-white/90 rounded-full min-w-[44px] min-h-[44px] w-10 h-10 md:w-10 md:h-10 flex items-center justify-center shadow-md z-10 hover:bg-white active:scale-95 transition-all"
            aria-label="Next slide"
          >
            <ChevronRight size={24} className="text-gray-800" />
          </button>
        </div>
      </div>
    </section>
  );
};

export default FeaturesSlider;